import { ADITIONAL_SERVICE_LABELS } from "../../constants/commonConstants";
import { AditionalServiceCostCalculator } from "../../utils/aditionalServiceCostCalculator";
import { calculateCost } from "../../utils/costCalculator";
import { calculateLocalDeadline, currencyFormatter, formatUnitCount } from "../../utils/formatting";
import { getCustomerDiscountName, getDiscountDisplayName, seasonalDiscount } from "../../utils/seasonalDiscount";
import { useSelector } from "react-redux";

const getVisibleAdditionalServices = (services = []) =>
  services.filter((service) => !["Grammarly Report", "Writing Process Report"].includes(service?.label));

const formatSidebarPagesSlides = (pages, slides) => {
  const pageCount = Number(pages || 0);
  const slideCount = Number(slides || 0);

  if (pageCount > 0 && slideCount > 0) {
    return `${formatUnitCount(pageCount, "page")} + ${formatUnitCount(slideCount, "slide")}`;
  }

  if (pageCount > 0) {
    return formatUnitCount(pageCount, "page");
  }

  if (slideCount > 0) {
    return formatUnitCount(slideCount, "slide");
  }

  return "-";
};

const OrderSummarySidebar = ({
  formData = {},
  pages = 0,
  slides = 0,
  totalUnits,
  subject,
}) => {
  const referralDiscount = useSelector((state) => state.createOrder.referralDiscount);
  const loyaltyProgress = useSelector((state) => state.createOrder.loyaltyProgress);
  const referralExtraPercent = referralDiscount?.eligible
    ? Number(referralDiscount.referralPercent || 0)
    : 0;
  const referralTotalPercent = Number(
    referralDiscount?.totalPercent || seasonalDiscount.percent + referralExtraPercent
  );
  const loyaltyExtraPercent = !referralDiscount?.eligible && loyaltyProgress?.enabled
    ? Math.max(0, Number(loyaltyProgress?.currentLevel?.discountPercent || seasonalDiscount.percent) - seasonalDiscount.percent)
    : 0;
  const baseDiscountName = getCustomerDiscountName(loyaltyProgress);
  const billableUnits = Number(totalUnits ?? Number(pages || 0) + Number(slides || 0));
  const visibleServices = getVisibleAdditionalServices(formData.aditionalServices || []);
  const calculatedPriceDetails = calculateCost({
    academicLevel: formData.academicLevel,
    deadline: formData.deadline,
    spacing: formData.spacing,
    pages: billableUnits,
    typeOfPaper: formData.typeOfPaper,
    budgetIncrement: formData.budgetIncrement,
    writingType: formData.writingType,
    typeOfEnglish: formData.typeOfEnglish,
    aditionalServices: formData.aditionalServices,
    returnDetails: true,
    additionalDiscountPercent: referralExtraPercent || loyaltyExtraPercent,
    additionalDiscountName: referralExtraPercent ? "New customer referral discount" : baseDiscountName,
    additionalDiscountType: referralExtraPercent ? "referral" : "loyalty",
    baseDiscountName,
  });
  const priceDetails = typeof calculatedPriceDetails === "object"
    ? calculatedPriceDetails
    : { orderCost: 0, serviceBaseOrderCost: 0, costPerPage: 0, totalCost: 0, discountedTotalCost: 0, discount: null };
  const pagesSlidesValue = formatSidebarPagesSlides(pages, slides);
  const showDiscount = seasonalDiscount.enabled && priceDetails.discount?.amount > 0;
  const summaryItems = [
    { label: "Type", value: formData.writingType || "-" },
    { label: "Paper", value: formData.typeOfPaper || "-" },
    { label: "Level", value: formData.academicLevel || "-" },
    { label: "English", value: formData.typeOfEnglish || "-" },
    subject ? { label: "Subject", value: subject } : null,
    { label: "Pages / Slides", value: pagesSlidesValue },
    { label: "Deadline", value: formData.deadline ? calculateLocalDeadline(formData.deadline) : "-" },
  ].filter(Boolean);

  const getAdditionalServiceDetail = (service) => {
    if (service?.label === ADITIONAL_SERVICE_LABELS.FIGURES) {
      const figures = formData.numberOfFigures || service?.perPageMultiplier;
      return figures ? `${figures} figure${Number(figures) === 1 ? "" : "s"} requested` : "";
    }

    if (service?.label === ADITIONAL_SERVICE_LABELS.SPECIFIC_WRITER) {
      return formData.requestedWriterId ? `Writer ID requested: ${formData.requestedWriterId}` : "";
    }

    return "";
  };

  const getAdditionalServiceCost = (service) => {
    const serviceCost = Number(
      AditionalServiceCostCalculator(
        service,
        priceDetails.serviceBaseOrderCost ?? priceDetails.orderCost,
        priceDetails.costPerPage,
        billableUnits
      )
    );

    return currencyFormatter.format(Number.isFinite(serviceCost) ? serviceCost.toFixed(2) : 0);
  };

  return (
    <aside className="sticky top-6 hidden w-[280px] shrink-0 self-start rounded-[16px] border border-medium-blue bg-white/95 p-3 font-inter shadow-[0_16px_36px_rgba(15,23,42,0.08)] 1600-br:block">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-shades">
          Order Summary
        </p>
        <h3 className="mt-0.5 text-[16px] font-semibold leading-6 text-primary">
          Your selections
        </h3>
      </div>

      <div className="mt-3 divide-y divide-medium-gray rounded-[12px] border border-medium-gray bg-soft-blue px-3 py-1.5">
        {summaryItems.map((item) => (
          <div key={item.label} className="grid grid-cols-[72px_minmax(0,1fr)] gap-2 py-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray">
              {item.label}
            </p>
            <p className="break-words text-right text-[12px] font-semibold leading-4 text-secondary">
              {item.value}
            </p>
          </div>
        ))}
      </div>

      {visibleServices.length > 0 && (
        <div className="mt-3 rounded-[12px] border border-medium-gray bg-white px-3 py-2.5">
          <p className="text-[12px] font-semibold leading-5 text-primary">
            Additional Services
          </p>
          <div className="mt-1.5 divide-y divide-medium-gray">
            {visibleServices.map((service) => (
              <div key={service.label} className="py-1.5 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] font-medium leading-4 text-secondary">
                    {service.label}
                  </p>
                  <span className="shrink-0 text-[11px] font-semibold leading-4 text-blue-shades">
                    {getAdditionalServiceCost(service)}
                  </span>
                </div>
                {getAdditionalServiceDetail(service) && (
                  <p className="mt-1 text-[11px] font-medium leading-4 text-gray">
                    {getAdditionalServiceDetail(service)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {referralDiscount?.eligible && (
        <div className="mt-3 rounded-[10px] border border-medium-blue bg-soft-blue px-3 py-2 text-[11px] font-medium leading-4 text-blue-shades">
          <span className="sm:hidden">
            <span className="font-semibold">Referral offer active:</span>{" "}
            {referralTotalPercent}% off your first order. Applied automatically.
          </span>
          <span className="hidden sm:inline">
            <span className="font-semibold">First-order referral offer:</span>{" "}
            You qualify for {referralTotalPercent}% off this order ({seasonalDiscount.percent}% {baseDiscountName} + {referralExtraPercent}% referral discount). Applied automatically.
          </span>
        </div>
      )}

      {!referralDiscount?.eligible && loyaltyExtraPercent > 0 && (
        <div className="mt-3 rounded-[10px] border border-medium-blue bg-soft-blue px-3 py-2 text-[11px] font-medium leading-4 text-blue-shades">
          <span className="font-semibold">Loyalty benefit:</span>{" "}
          {loyaltyProgress.currentLevel.discountPercent}% off this order. Applied automatically.
        </div>
      )}

      <div className="mt-3 rounded-[14px] border border-medium-blue bg-soft-blue px-3 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[12px] font-semibold leading-5 text-secondary">
            Order total
          </span>
          <div className="text-right">
            {showDiscount && (
              <span className="block text-[12px] font-medium leading-4 text-blue-shades/70 line-through">
                {currencyFormatter.format(priceDetails.totalCost || 0)}
              </span>
            )}
            <span className="block text-[17px] font-semibold leading-6 text-blue-shades">
              {currencyFormatter.format(priceDetails.discountedTotalCost || 0)}
            </span>
          </div>
        </div>
        {showDiscount && (
          <p className="mt-2 text-[11px] font-semibold leading-4 text-blue-shades">
            You saved {currencyFormatter.format(priceDetails.discount.amount)} with {getDiscountDisplayName(priceDetails.discount?.name, seasonalDiscount.name)}
          </p>
        )}
        {Number(formData.budgetIncrement || 0) > 0 && (
          <p className="mt-2 text-[11px] font-medium leading-4 text-gray">
            Additional writer budget: {currencyFormatter.format(formData.budgetIncrement)}.
          </p>
        )}
      </div>
    </aside>
  );
};

export default OrderSummarySidebar;
